import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaIdCard,
  FaLayerGroup,
  FaSave,
  FaTimes,
  FaTrophy,
  FaUserPlus,
  FaVenusMars,
  FaWeight,
} from "react-icons/fa";

import { addCompetiteur } from "./../../../services/competiteursServices";
import { getCompetitionById } from "./../../../services/competitionsServices";
import { getCompetitionCompetiteurs } from "./../../../services/competiteursServices";
import { getCompetitionSettingsByCompetitionId } from "./../../../services/competitionsettingsServices";
import { toast } from "react-toastify";

const ALL_DISCIPLINES = [
  "Judo",
  "Ne-Waza",
  "JJB",
  "Jujitsu Combat",
  "Jujitsu Duo",
  "Arbitre",
  "Commissaire Sportif",
  "Judo Adapté"
];

const BELT_BASE_PATH = "/img/belt";

const GRADE_OPTIONS = [
  {
    label: "Ceinture blanche",
    value: "blanche",
    image: "ceinture_judo_blanche.png",
  },
  {
    label: "Ceinture blanche 1 liseré",
    value: "blanche 1 liseré",
    image: "ceinture_judo_blanche_1L.png",
  },
  {
    label: "Ceinture blanche 2 liserés",
    value: "blanche 2 liserés",
    image: "ceinture_judo_blanche_2L.png",
  },
  {
    label: "Ceinture blanche-jaune",
    value: "blanche-jaune",
    image: "ceinture_judo_blanche_jaune.png",
  },
  {
    label: "Ceinture jaune",
    value: "jaune",
    image: "ceinture_judo_jaune.png",
  },
  {
    label: "Ceinture jaune-orange",
    value: "jaune-orange",
    image: "ceinture_judo_jaune_orange.png",
  },
  {
    label: "Ceinture orange",
    value: "orange",
    image: "ceinture_judo_orange.png",
  },
  {
    label: "Ceinture orange-verte",
    value: "orange-verte",
    image: "ceinture_judo_orange_verte.png",
  },
  {
    label: "Ceinture verte",
    value: "verte",
    image: "ceinture_judo_verte.png",
  },
  {
    label: "Ceinture verte-bleue",
    value: "verte-bleue",
    image: "ceinture_judo_verte_bleue.png",
  },
  {
    label: "Ceinture bleue",
    value: "bleue",
    image: "ceinture_judo_bleue.png",
  },
  {
    label: "Ceinture bleue-marron",
    value: "bleue-marron",
    image: "ceinture_judo_bleue_marron.png",
  },
  {
    label: "Ceinture marron",
    value: "marron",
    image: "ceinture_judo_marron.png",
  },
  {
    label: "Ceinture noire",
    value: "noire",
    image: "ceinture_judo_noire.png",
  },
  {
    label: "1er Dan",
    value: "1er Dan",
    image: "ceinture_judo_1D.png",
  },
  {
    label: "2e Dan",
    value: "2e Dan",
    image: "ceinture_judo_2D.png",
  },
  {
    label: "3e Dan",
    value: "3e Dan",
    image: "ceinture_judo_3D.png",
  },
  {
    label: "4e Dan",
    value: "4e Dan",
    image: "ceinture_judo_4D.png",
  },
  {
    label: "5e Dan",
    value: "5e Dan",
    image: "ceinture_judo_5D.png",
  },
];

const normalize = (str = "") =>
  String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();

const getGradeClasses = (g = "") => {
  const s = normalize(g);

  if (!s) {
    return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-white/10";
  }

  if (s.includes("blanche")) {
    return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-white/10";
  }

  if (s.includes("jaune")) {
    return "bg-yellow-100 text-yellow-800 ring-yellow-200 dark:bg-yellow-500/15 dark:text-yellow-200 dark:ring-yellow-300/20";
  }

  if (s.includes("orange")) {
    return "bg-orange-100 text-orange-800 ring-orange-200 dark:bg-orange-500/15 dark:text-orange-200 dark:ring-orange-300/20";
  }

  if (s.includes("verte")) {
    return "bg-green-100 text-green-800 ring-green-200 dark:bg-green-500/15 dark:text-green-200 dark:ring-green-300/20";
  }

  if (s.includes("bleue") || s.includes("bleu")) {
    return "bg-blue-100 text-blue-800 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-200 dark:ring-blue-300/20";
  }

  if (s.includes("marron") || s.includes("brun")) {
    return "bg-amber-900 text-amber-100 ring-amber-800 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-300/20";
  }

  if (s.includes("noire") || s.includes("noir") || s.includes("dan")) {
    return "bg-slate-950 text-white ring-slate-800 dark:bg-slate-200 dark:text-slate-950 dark:ring-white/20";
  }

  return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-white/10";
};

const coerceGradeValue = (raw = "") => {
  if (!raw) return "";

  const trimmed = String(raw).trim();

  if (/^ceinture\s+/i.test(trimmed)) {
    return trimmed.replace(/^ceinture\s+/i, "");
  }

  return trimmed;
};

const getGradeOption = (value = "") => {
  const coerced = coerceGradeValue(value);
  return (
    GRADE_OPTIONS.find((option) => option.value === coerced) || {
      label: coerced || "Aucun grade",
      value: coerced || "",
      image: "ceinture_inconnue.png",
    }
  );
};

const getBeltImageSrc = (value = "") => {
  const option = getGradeOption(value);
  return `${BELT_BASE_PATH}/${option.image || "ceinture_inconnue.png"}`;
};

const parseWeight = (value) => {
  if (value === "" || value === null || value === undefined) return null;

  const numeric = Number(String(value).replace(",", "."));

  if (!Number.isFinite(numeric)) return Number.NaN;

  return numeric;
};

const FieldLabel = ({ icon: Icon, children }) => (
  <label className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
    {Icon && <Icon className="h-3.5 w-3.5 text-orange-500" />}
    {children}
  </label>
);

const inputClass =
  "min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:ring-orange-500/20";

const selectClass =
  "min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-200 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:ring-orange-500/20";

const CreateCompetiteurModal = ({
  isOpen,
  onClose,
  competitionId,
  refreshList,
}) => {
  const [disciplines, setDisciplines] = useState(ALL_DISCIPLINES);
  const [saving, setSaving] = useState(false);

  const [competiteurData, setCompetiteurData] = useState({
    nom_competiteur: "",
    prenom_competiteur: "",
    ddn_competiteur: "",
    licence_competiteur: "",
    club_competiteur: "",
    poids_competiteur: "",
    genre_competiteur: "M",
    discipline_competiteur: "Judo",
    grade_competiteur: "",
    competition_id: competitionId,
    is_present: true,
    is_distributed: false,
    distribution_id: null,
  });

  const [quotaInfo, setQuotaInfo] = useState({
    quota: null,
    total: 0,
    loading: false,
  });

  const quota = quotaInfo.quota;
  const totalInscrits = quotaInfo.total;
  const quotaActive = quota && quota > 0;
  const quotaReached = quotaActive && totalInscrits >= quota;
  const nearFull = quotaActive && !quotaReached && totalInscrits >= quota * 0.8;

  const selectedGrade = useMemo(
    () => getGradeOption(competiteurData.grade_competiteur),
    [competiteurData.grade_competiteur]
  );

  const selectedBeltSrc = useMemo(
    () => getBeltImageSrc(competiteurData.grade_competiteur),
    [competiteurData.grade_competiteur]
  );

  const quotaTone = quotaReached
    ? {
        card: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100",
        bar: "bg-rose-500",
        label: "Quota dépassé",
        icon: FaExclamationTriangle,
      }
    : nearFull
    ? {
        card: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-100",
        bar: "bg-amber-400",
        label: "Quasi complet",
        icon: FaExclamationTriangle,
      }
    : {
        card: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-100",
        bar: "bg-emerald-500",
        label: "Places disponibles",
        icon: FaCheckCircle,
      };

  useEffect(() => {
    if (isOpen) {
      setCompetiteurData({
        nom_competiteur: "",
        prenom_competiteur: "",
        ddn_competiteur: "",
        licence_competiteur: "",
        club_competiteur: "",
        poids_competiteur: "",
        genre_competiteur: "M",
        discipline_competiteur: "Judo",
        grade_competiteur: "",
        competition_id: competitionId,
        is_present: true,
        is_distributed: false,
        distribution_id: null,
      });
    }
  }, [isOpen, competitionId]);

  useEffect(() => {
    if (!isOpen || !competitionId) return;

    let cancelled = false;

    const fetchDisciplines = async () => {
      try {
        const competition = await getCompetitionById(competitionId);
        const raw = competition?.disciplines_competition;
        const parsed = typeof raw === "string" ? JSON.parse(raw || "[]") : raw;

        const allowed =
          Array.isArray(parsed) && parsed.length > 0
            ? parsed.filter((discipline) => ALL_DISCIPLINES.includes(discipline))
            : [];

        const finalList = allowed.length > 0 ? allowed : ALL_DISCIPLINES;

        if (cancelled) return;

        setDisciplines(finalList);

        setCompetiteurData((prev) => ({
          ...prev,
          discipline_competiteur: finalList.includes(
            prev.discipline_competiteur
          )
            ? prev.discipline_competiteur
            : finalList[0],
        }));
      } catch {
        if (!cancelled) {
          setDisciplines(ALL_DISCIPLINES);
        }
      }
    };

    fetchDisciplines();

    return () => {
      cancelled = true;
    };
  }, [isOpen, competitionId]);

  useEffect(() => {
    if (!isOpen || !competitionId) return;

    let cancelled = false;

    const fetchQuota = async () => {
      try {
        setQuotaInfo((prev) => ({
          ...prev,
          loading: true,
        }));

        const [settingsData, competiteursData] = await Promise.all([
          getCompetitionSettingsByCompetitionId(competitionId),
          getCompetitionCompetiteurs(competitionId),
        ]);

        if (cancelled) return;

        const settings =
          Array.isArray(settingsData) && settingsData.length > 0
            ? settingsData[0]
            : settingsData;

        const quotaValue =
          settings && settings.quota_competition != null
            ? Number(settings.quota_competition)
            : null;

        setQuotaInfo({
          quota: Number.isFinite(quotaValue) ? quotaValue : null,
          total: Array.isArray(competiteursData) ? competiteursData.length : 0,
          loading: false,
        });
      } catch (error) {
        console.error("Erreur chargement quota compétition:", error);

        if (!cancelled) {
          setQuotaInfo((prev) => ({
            ...prev,
            loading: false,
          }));
        }
      }
    };

    fetchQuota();

    return () => {
      cancelled = true;
    };
  }, [isOpen, competitionId]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setCompetiteurData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectGrade = (value) => {
    setCompetiteurData((prev) => ({
      ...prev,
      grade_competiteur: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) return;

    try {
      setSaving(true);

      const poids = parseWeight(competiteurData.poids_competiteur);

      if (poids !== null && (Number.isNaN(poids) || poids < 0 || poids > 300)) {
        toast.error("Poids invalide (0–300 kg).");
        return;
      }

      if (quotaActive && totalInscrits + 1 > quota) {
        toast.warn(
          "Quota de la compétition dépassé : ce compétiteur sera considéré sur liste d’attente."
        );
      } else if (quotaActive && nearFull) {
        toast.info("Attention : la compétition approche de sa capacité maximale.");
      }

      const payload = {
        ...competiteurData,
        poids_competiteur: poids,
        discipline_competiteur: disciplines.includes(
          competiteurData.discipline_competiteur
        )
          ? competiteurData.discipline_competiteur
          : disciplines[0] || "Judo",
        grade_competiteur:
          coerceGradeValue(competiteurData.grade_competiteur) || null,
      };

      await addCompetiteur(payload);

      toast.success("Compétiteur créé avec succès");
      refreshList?.();
      onClose?.();
    } catch {
      toast.error("Erreur lors de la création du compétiteur");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const QuotaIcon = quotaTone.icon;

  return (
    <div
      className="fixed inset-0 z-[99999] flex h-[100dvh] w-full items-end justify-center overflow-hidden bg-slate-950/70 px-2 backdrop-blur-sm sm:items-center sm:px-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Créer un compétiteur"
        className="
          flex max-h-[96dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[2rem]
          border border-white/20 bg-white text-slate-950 shadow-2xl
          dark:border-white/10 dark:bg-slate-950 dark:text-white
          sm:max-h-[92dvh] sm:rounded-[2rem]
        "
      >
        <header className="relative shrink-0 overflow-hidden border-b border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 px-4 py-4 text-white sm:px-6 sm:py-5">
          <div className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-orange-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-16 h-48 w-48 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.26em] text-orange-300">
                Pesée compétition
              </p>

              <h2 className="mt-1 flex items-center gap-2 text-xl font-bold leading-tight sm:text-2xl">
                <FaUserPlus className="h-5 w-5 text-orange-300" />
                Créer un compétiteur
              </h2>

              <p className="mt-1 max-w-2xl text-sm text-slate-300">
                Ajoutez rapidement un compétiteur, son club, son poids et son
                grade avec prévisualisation de la ceinture.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
              aria-label="Fermer"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
            {quotaActive && !quotaInfo.loading && (
              <div className={`mb-4 rounded-2xl border p-4 ${quotaTone.card}`}>
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/70 text-current shadow-sm dark:bg-white/10">
                    <QuotaIcon className="h-4 w-4" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                      <p className="font-bold">
                        {totalInscrits} inscrit{totalInscrits > 1 ? "s" : ""} /{" "}
                        {quota} autorisé{quota > 1 ? "s" : ""}
                      </p>

                      <p className="text-xs font-bold uppercase tracking-[0.16em]">
                        {quotaTone.label}
                      </p>
                    </div>

                    <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${quotaTone.bar}`}
                        style={{
                          width: `${Math.min(
                            100,
                            (totalInscrits / quota) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
              <div className="min-w-0 space-y-5">
                <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/20">
                      <FaIdCard className="h-4 w-4" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                        Identité du compétiteur
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Informations principales pour l’inscription.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <FieldLabel>Nom</FieldLabel>
                      <input
                        className={inputClass}
                        name="nom_competiteur"
                        value={competiteurData.nom_competiteur}
                        onChange={handleChange}
                        placeholder="Ex : DUPONT"
                        required
                      />
                    </div>

                    <div>
                      <FieldLabel>Prénom</FieldLabel>
                      <input
                        className={inputClass}
                        name="prenom_competiteur"
                        value={competiteurData.prenom_competiteur}
                        onChange={handleChange}
                        placeholder="Ex : Léa"
                        required
                      />
                    </div>

                    <div>
                      <FieldLabel icon={FaCalendarAlt}>
                        Date de naissance
                      </FieldLabel>
                      <input
                        type="date"
                        className={inputClass}
                        name="ddn_competiteur"
                        value={competiteurData.ddn_competiteur}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div>
                      <FieldLabel icon={FaIdCard}>Licence</FieldLabel>
                      <input
                        className={inputClass}
                        name="licence_competiteur"
                        value={competiteurData.licence_competiteur}
                        onChange={handleChange}
                        placeholder="Ex : M10071971VICTO01"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <FieldLabel>Club</FieldLabel>
                      <input
                        className={inputClass}
                        name="club_competiteur"
                        value={competiteurData.club_competiteur}
                        onChange={handleChange}
                        placeholder="Nom du club"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg shadow-slate-950/20 dark:bg-white dark:text-slate-950">
                      <FaTrophy className="h-4 w-4" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                        Compétition
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Discipline, sexe, poids et grade.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <FieldLabel icon={FaWeight}>Poids</FieldLabel>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Ex : 40,1"
                        className={inputClass}
                        name="poids_competiteur"
                        value={competiteurData.poids_competiteur}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <FieldLabel icon={FaVenusMars}>Genre</FieldLabel>
                      <select
                        className={selectClass}
                        name="genre_competiteur"
                        value={competiteurData.genre_competiteur}
                        onChange={handleChange}
                        required
                      >
                        <option value="M">Masculin</option>
                        <option value="F">Féminin</option>
                      </select>
                    </div>

                    <div>
                      <FieldLabel icon={FaLayerGroup}>Discipline</FieldLabel>
                      <select
                        className={selectClass}
                        name="discipline_competiteur"
                        value={competiteurData.discipline_competiteur}
                        onChange={handleChange}
                        required
                      >
                        {disciplines.map((discipline) => (
                          <option key={discipline} value={discipline}>
                            {discipline}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <FieldLabel icon={FaTrophy}>Grade</FieldLabel>
                      <select
                        className={selectClass}
                        name="grade_competiteur"
                        value={competiteurData.grade_competiteur}
                        onChange={handleChange}
                      >
                        <option value="">Aucun grade</option>
                        {GRADE_OPTIONS.map((grade) => (
                          <option key={grade.value} value={grade.value}>
                            {grade.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03] lg:hidden">
                  <GradePreview
                    selectedGrade={selectedGrade}
                    selectedBeltSrc={selectedBeltSrc}
                    currentValue={competiteurData.grade_competiteur}
                    onSelect={handleSelectGrade}
                  />
                </div>
              </div>

              <aside className="hidden min-w-0 lg:block">
                <div className="sticky top-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.03]">
                  <GradePreview
                    selectedGrade={selectedGrade}
                    selectedBeltSrc={selectedBeltSrc}
                    currentValue={competiteurData.grade_competiteur}
                    onSelect={handleSelectGrade}
                  />
                </div>
              </aside>
            </div>
          </div>

          <footer className="shrink-0 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:hover:bg-white/10"
              >
                <FaTimes className="h-4 w-4" />
                Fermer
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 py-2 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave className="h-4 w-4" />
                {saving ? "Création..." : "Créer le compétiteur"}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
};

const GradePreview = ({
  selectedGrade,
  selectedBeltSrc,
  currentValue,
  onSelect,
}) => {
  return (
    <div>
      <div className="mb-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-orange-600 dark:text-orange-300">
          Grade sélectionné
        </p>

        <h3 className="mt-1 text-lg font-bold text-slate-950 dark:text-white">
          {selectedGrade?.label || "Aucun grade"}
        </h3>
      </div>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 dark:border-white/10 dark:from-slate-900 dark:to-slate-950">
        <div className="flex min-h-28 items-center justify-center rounded-2xl bg-white p-3 shadow-inner dark:bg-slate-900">
          <img
            src={selectedBeltSrc}
            alt={selectedGrade?.label || "Ceinture inconnue"}
            className="max-h-24 w-full object-contain"
            onError={(event) => {
              event.currentTarget.src = `${BELT_BASE_PATH}/ceinture_inconnue.png`;
            }}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ring-1 ${getGradeClasses(
              currentValue
            )}`}
          >
            {selectedGrade?.label || "Aucun grade"}
          </span>

          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
            {currentValue || "non défini"}
          </span>
        </div>
      </div>

      <div className="mt-4">
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
          Sélection rapide
        </p>

        <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-2">
          {GRADE_OPTIONS.map((grade) => {
            const active = currentValue === grade.value;

            return (
              <button
                key={grade.value}
                type="button"
                onClick={() => onSelect(grade.value)}
                className={`group flex min-h-16 items-center gap-2 rounded-2xl border p-2 text-left transition ${
                  active
                    ? "border-orange-400 bg-orange-50 text-orange-800 shadow-sm dark:bg-orange-500/15 dark:text-orange-200"
                    : "border-slate-200 bg-white text-slate-700 hover:border-orange-200 hover:bg-orange-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                }`}
              >
                <span className="flex h-10 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-50 p-1 dark:bg-white/5">
                  <img
                    src={`${BELT_BASE_PATH}/${grade.image}`}
                    alt={grade.label}
                    className="max-h-8 w-full object-contain transition group-hover:scale-105"
                    onError={(event) => {
                      event.currentTarget.src = `${BELT_BASE_PATH}/ceinture_inconnue.png`;
                    }}
                  />
                </span>

                <span className="min-w-0 text-xs font-bold leading-tight">
                  {grade.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CreateCompetiteurModal;